import type { AxiosInstance, AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import { isLikelyBot } from '@core/services/bot-detection';

const MAX_429_RETRY_COUNT = 2;
const BASE_DELAY_MS = 1000;
const MAX_RETRY_AFTER_MS = 10_000;
const JITTER_FACTOR = 0.25;

/**
 * Calculate the retry delay in ms.
 * Returns -1 if the Retry-After value exceeds the cap (meaning: do not retry).
 */
export function calculateRetryDelay(
  attempt: number,
  headers: Record<string, string>
): number {
  const retryAfterRaw = headers['retry-after'];

  if (retryAfterRaw) {
    const retryAfterSeconds = Number(retryAfterRaw);
    if (!Number.isNaN(retryAfterSeconds)) {
      const retryAfterMs = retryAfterSeconds * 1000;
      return retryAfterMs <= MAX_RETRY_AFTER_MS ? retryAfterMs : -1;
    }
  }

  // Exponential backoff with jitter
  const exponentialDelay = BASE_DELAY_MS * Math.pow(2, attempt);
  const jitter = exponentialDelay * JITTER_FACTOR * (2 * Math.random() - 1);
  return Math.min(exponentialDelay + jitter, MAX_RETRY_AFTER_MS);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function configure429Retry(http: AxiosInstance): void {
  const isBotUser = isLikelyBot();

  http.interceptors.response.use(undefined, async (error: AxiosError) => {
    if (
      !isAxiosError(error) ||
      error.response?.status !== 429 ||
      !error.config
    ) {
      throw error;
    }

    // Skip retry for bots and opt-out requests
    if (isBotUser || error.config.noRetryOn429) {
      throw error;
    }

    const retryCount = error.config._429RetryCount ?? 0;
    if (retryCount >= MAX_429_RETRY_COUNT) {
      throw error;
    }

    const responseHeaders = (error.response?.headers ?? {}) as Record<string, string>;
    const delay = calculateRetryDelay(retryCount, responseHeaders);

    // If server asks to wait longer than our cap, don't retry
    if (delay < 0) {
      throw error;
    }

    await sleep(delay);

    error.config._429RetryCount = retryCount + 1;
    // Suppress progress bar flicker on retries
    error.config.noProgressBar = true;

    return http(error.config);
  });
}
